import {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { useWindowSize } from 'usehooks-ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  GoogleOAuthProvider,
  GoogleLogin,
  CredentialResponse,
} from '@react-oauth/google';

import { STEP, ACCOUNT_ERRORS } from '~/constants/checkout';
import { CheckoutStepProps, SetStepErrorMessageType } from '~/types/checkout';
import { FacebookLoginResponseProps } from '~/types/user';
import useUser from '~/lib/user';
import { useCart } from '~/lib/cart';
import { fbLoginFunction, handleGoogleLogin } from '~/lib/util/login-helpers';

interface SocialLoginProps {
  setStep: (step: CheckoutStepProps) => void;
  setStepErrorMessage: SetStepErrorMessageType;
  width?: number;
  isSubmittingAccount: boolean;
  setIsSubmittingAccount: (isSubmittingAccount: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function FacebookButton({
  isSubmittingAccount,
  setIsSubmittingAccount,
  setStep,
  setStepErrorMessage,
}: Readonly<SocialLoginProps>): ReactElement {
  const { facebookSignIn } = useUser();
  const { mutateItems } = useCart();

  useEffect(() => {
    // Load the Facebook SDK
    ((d: Document, s: string, id: string): void => {
      const fjs: Element = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      const js: HTMLElement = d.createElement(s);
      js.id = id;
      // @ts-ignore
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      // @ts-ignore
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');

    // Initialize the Facebook SDK
    // @ts-ignore
    window.fbAsyncInit = () => {
      // @ts-ignore
      window.FB.init({
        appId: process.env.NEXT_PUBLIC_FACEBOOK_LOGIN_APP_ID ?? '',
        cookie: true,
        xfbml: true,
        version: 'v16.0',
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only run once

  const handleFacebookLogin = () => {
    // @ts-ignore
    if (!window.FB) {
      setStepErrorMessage(STEP.ACCOUNT, ACCOUNT_ERRORS.FACEBOOK_API);
      return;
    }

    // @ts-ignore
    window.FB.login(
      (fbResponse: FacebookLoginResponseProps) =>
        fbLoginFunction(
          fbResponse,
          isSubmittingAccount,
          setStepErrorMessage,
          setIsSubmittingAccount,
          facebookSignIn,
          mutateItems,
          setStep,
        ),
      { scope: 'email,public_profile' },
    );
  };

  const facebookClassName = clsx(
    'w-full text-center',
    isSubmittingAccount && 'pointer-events-none',
  );

  return (
    <div className={facebookClassName}>
      <button
        className="flex w-full flex-row items-center justify-center rounded border border-[#1877F2] bg-[#1877F2] font-semibold text-white hover:bg-[#1889f2]"
        onClick={handleFacebookLogin}
        data-testid="facebook-btn"
      >
        <FontAwesomeIcon
          icon={faFacebook as IconProp}
          height="24"
          width="24"
          className="mx-2 h-6"
        />
        <div className="py-2">Continue with Facebook</div>
      </button>
    </div>
  );
}

function GoogleButton({
  isSubmittingAccount,
  setIsSubmittingAccount,
  setStep,
  setStepErrorMessage,
  width,
}: Readonly<SocialLoginProps>): ReactElement {
  const { googleSignIn } = useUser();
  const { mutateItems } = useCart();

  const googleClass = clsx(
    'flex w-full justify-center text-center',
    isSubmittingAccount && 'pointer-events-none',
  );

  return (
    <div className={googleClass} data-testid="google-wrapper">
      <GoogleOAuthProvider
        clientId={process.env.NEXT_PUBLIC_GOOGLE_LOGIN_APP_ID ?? ''}
      >
        <GoogleLogin
          onSuccess={(credentialResponse: CredentialResponse) => {
            handleGoogleLogin(
              credentialResponse,
              isSubmittingAccount,
              setStepErrorMessage,
              setIsSubmittingAccount,
              googleSignIn,
              mutateItems,
              setStep,
            );
          }}
          onError={() => {
            setStepErrorMessage(STEP.ACCOUNT, ACCOUNT_ERRORS.GOOGLE_LOGIN);
          }}
          size="large"
          text="continue_with"
          logo_alignment="center"
          width={width}
        />
      </GoogleOAuthProvider>
    </div>
  );
}

export default function SocialLogin({
  isSubmittingAccount,
  setIsSubmittingAccount,
  setStep,
  setStepErrorMessage,
}: Readonly<SocialLoginProps>): ReactElement {
  const loginContainer = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  const calcGoogleBtnWidth = useCallback((): number => {
    if (!loginContainer?.current?.clientWidth || !width) return 180;

    let containerWidth = loginContainer.current.clientWidth;
    let btnWidth;

    // mobile vs desktop breakpoints
    if (width < 768) {
      btnWidth = Math.min(containerWidth, 400);
    } else {
      containerWidth -= 16; // account for gap-2 (16px) on container
      btnWidth = Math.min(containerWidth / 2, 400);
    }
    return Math.round(btnWidth);
  }, [width]);

  const [googleButtonWidth, setGoogleButtonWidth] = useState(
    calcGoogleBtnWidth(),
  );

  useLayoutEffect(() => {
    const width = calcGoogleBtnWidth();
    setGoogleButtonWidth(width);
  }, [width, loginContainer?.current?.clientWidth, calcGoogleBtnWidth]);

  return (
    <div
      className="my-4 flex flex-col items-center justify-center gap-2 md:flex-row md:gap-4"
      ref={loginContainer}
    >
      <GoogleButton
        isSubmittingAccount={isSubmittingAccount}
        setIsSubmittingAccount={setIsSubmittingAccount}
        setStep={setStep}
        setStepErrorMessage={setStepErrorMessage}
        width={googleButtonWidth}
      />
      <FacebookButton
        isSubmittingAccount={isSubmittingAccount}
        setIsSubmittingAccount={setIsSubmittingAccount}
        setStep={setStep}
        setStepErrorMessage={setStepErrorMessage}
      />
    </div>
  );
}
